import type { Asset, Rect, ShaderDef } from '@flobi/artwork-core';
import type { SketchAsk } from './videoSketches';

/**
 * A video export drawn and encoded in this browser: MP4 through WebCodecs, GIF through gifenc.
 *
 * The drawing runs in a worker on an OffscreenCanvas, on the GPU when the browser gives one
 * (WebGL, the canvas's own path, so a Lumens effect costs what it costs on screen), and a worker
 * is what keeps it going behind other tabs: a browser slows a hidden page's timers and stops its
 * animation frames, but not its workers. It is the export page's (`ExportRoute`), which says to
 * keep it open; the render service draws the same frames for the agent and the API.
 */

export interface VideoTarget {
  readonly name: string;
  readonly artboardId: string;
  /** Where the artboard is on its page, in document units. */
  readonly box: Rect;
  /** How long it plays (`playableLength`). */
  readonly seconds: number;
}

export interface VideoSnapshot {
  readonly page: Uint8Array;
  readonly shaders: [string, ShaderDef][];
  readonly assets: [string, Asset][];
  readonly fonts: { family: string; url: string }[];
  readonly proof?: { lut: Uint8Array; size: number };
  readonly targets: VideoTarget[];
  readonly scale: number;
  readonly fps: number;
  readonly format: 'mp4' | 'gif';
}

export type VideoReply =
  | { type: 'progress'; done: number; total: number; message: string; gpu?: boolean }
  /** A small picture of a frame just drawn: `slot` places it on the page's filmstrip, when it is one. */
  | { type: 'preview'; target: number; frame: number; frames: number; slot: number | null; bitmap: ImageBitmap }
  /** The worker needs the page: a sketch's frame, which only a page can run. */
  | { type: 'ask'; id: number; ask: SketchAsk }
  | { type: 'done'; bytes: Uint8Array; files: number }
  | { type: 'error'; message: string };

/** The page's answer to an `ask`. */
export type VideoAnswer = { type: 'answer'; id: number; bitmap: ImageBitmap | null; error?: string };

/** Filmstrip slots the export page shows for each artboard. */
export const FILMSTRIP_SLOTS = 24;

export interface VideoPreview {
  readonly target: number;
  readonly frame: number;
  readonly frames: number;
  readonly slot: number | null;
  readonly bitmap: ImageBitmap;
}

export interface VideoProgress {
  /** 0 to 1, in frames across every artboard. */
  readonly fraction: number;
  readonly message: string;
  readonly gpu?: boolean;
}

/** The widest or tallest video: the largest surface a graphics card draws in one go. */
export const VIDEO_EDGE = 16_384;

/** A video's frame count, as the render service counts it: one a step, the last landing on the end. */
export function frameCount(seconds: number, fps: number): number {
  return Math.max(1, Math.round(seconds * fps) + 1);
}

/** Pixels for an artboard at a scale; refused past what a graphics card can draw at once. */
export function videoSize(box: Rect, scale: number): { width: number; height: number } {
  const width = Math.max(1, Math.ceil(box.width * scale));
  const height = Math.max(1, Math.ceil(box.height * scale));
  if (width > VIDEO_EDGE || height > VIDEO_EDGE) {
    throw new Error(`That video would be ${width} × ${height} pixels, more than ${VIDEO_EDGE.toLocaleString('en')} px on a side. Choose a smaller size.`);
  }
  return { width, height };
}

/** H.264 in 4:2:0 needs even sides: padded by a pixel, as the service pads, never resampled. */
export function evenSize(size: { width: number; height: number }): { width: number; height: number } {
  return { width: size.width + (size.width & 1), height: size.height + (size.height & 1) };
}

/**
 * The encoders tried, best first. H.264 plays everywhere: High, Main and Baseline at level 5.2,
 * then 6.0 and 6.2 for pages past 4K. HEVC after it, for a page H.264 here will not take (a Mac
 * encodes HEVC up to 8K; H.264 hardware often stops near 4096 px a side).
 */
const CODECS: { codec: string; kind: VideoCodecKind }[] = [
  ...['640034', '4d0034', '42e034', '64003c', '4d003c', '42e03c', '64003e', '4d003e'].map((level) => ({ codec: `avc1.${level}`, kind: 'avc' as const })),
  ...['L156', 'L180', 'L186'].map((level) => ({ codec: `hvc1.1.6.${level}.B0`, kind: 'hevc' as const })),
  // AV1 last: a software encoder that takes any size a graphics card draws, slower, and played by
  // browsers and recent Macs rather than everywhere.
  ...['13M', '16M', '19M'].map((level) => ({ codec: `av01.0.${level}.08`, kind: 'av1' as const })),
];

export type VideoCodecKind = 'avc' | 'hevc' | 'av1';
export interface PickedEncoder {
  readonly config: VideoEncoderConfig;
  readonly kind: VideoCodecKind;
}

/** A bitrate that keeps flat colour and type clean: about a third of a bit a pixel a frame. */
export function bitrateFor(width: number, height: number, fps: number): number {
  return Math.round(Math.min(60e6, Math.max(4e6, width * height * fps * 0.3)));
}

/** The first encoder this browser has for a frame this size, or null when it has none. */
export async function pickEncoder(width: number, height: number, fps: number): Promise<PickedEncoder | null> {
  if (typeof VideoEncoder === 'undefined') return null;
  const even = evenSize({ width, height });
  for (const { codec, kind } of CODECS) {
    // Length-prefixed samples and a description record (avcC / hvcC), as an MP4 holds them.
    const format = kind === 'avc' ? { avc: { format: 'avc' } } : kind === 'hevc' ? { hevc: { format: 'hevc' } } : {};
    const config = { codec, width: even.width, height: even.height, bitrate: bitrateFor(even.width, even.height, fps), framerate: fps, latencyMode: 'quality', ...format } as VideoEncoderConfig;
    try {
      const support = await VideoEncoder.isConfigSupported(config);
      if (support.supported) return { config: { ...config, ...support.config, ...format } as VideoEncoderConfig, kind };
    } catch {
      /* this one is refused outright: try the next */
    }
  }
  return null;
}

/**
 * Why this browser cannot make this video itself, or null when it can. GIF needs only a worker
 * and an OffscreenCanvas; MP4 needs an H.264, HEVC or AV1 encoder that takes every artboard's size.
 */
export async function localVideoBlocker(snapshot: Pick<VideoSnapshot, 'targets' | 'scale' | 'fps' | 'format'>): Promise<string | null> {
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return 'This browser cannot draw a video in the background.';
  if (snapshot.format === 'gif') return null;
  if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') return 'This browser has no video encoder.';
  for (const target of snapshot.targets) {
    const size = videoSize(target.box, snapshot.scale);
    if (!(await pickEncoder(size.width, size.height, snapshot.fps))) return `This browser cannot encode a ${size.width} × ${size.height} MP4.`;
  }
  return null;
}

/** Runs the export in a worker; terminating it is what cancels a frame half drawn. */
export function runVideoExport(
  snapshot: VideoSnapshot,
  signal: AbortSignal,
  onProgress: (progress: VideoProgress) => void,
  onPreview: (preview: VideoPreview) => void = (preview) => preview.bitmap.close(),
  createWorker = () => new Worker(new URL('./videoExport.worker.ts', import.meta.url), { type: 'module' }),
): Promise<{ bytes: Uint8Array; files: number }> {
  return new Promise((resolve, reject) => {
    signal.throwIfAborted();
    const worker = createWorker();
    let settled = false;
    // A sketch's code runs in a sandboxed page, which only this page can hold (videoSketchHost.ts).
    let sketches: Promise<import('./videoSketchHost').SketchAnswerer> | null = null;
    const answer = async (id: number, ask: SketchAsk) => {
      try {
        sketches ??= import('./videoSketchHost').then((module) => module.sketchAnswerer());
        const bitmap = await (await sketches).answer(ask);
        if (settled) return bitmap?.close();
        const reply: VideoAnswer = { type: 'answer', id, bitmap };
        worker.postMessage(reply, bitmap ? [bitmap] : []);
      } catch (error) {
        if (!settled) worker.postMessage({ type: 'answer', id, bitmap: null, error: (error as Error).message || 'The sketch failed.' } satisfies VideoAnswer);
      }
    };
    const finish = (error?: Error, made?: { bytes: Uint8Array; files: number }) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener('abort', abort);
      worker.terminate();
      void sketches?.then((made) => made.dispose());
      if (error) reject(error);
      else resolve(made!);
    };
    const abort = () => finish(new DOMException('Export cancelled.', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    worker.onmessage = (event: MessageEvent<VideoReply>) => {
      const reply = event.data;
      if (reply.type === 'ask') void answer(reply.id, reply.ask);
      else if (reply.type === 'preview') onPreview({ target: reply.target, frame: reply.frame, frames: reply.frames, slot: reply.slot, bitmap: reply.bitmap });
      else if (reply.type === 'progress') onProgress({ fraction: reply.total > 0 ? reply.done / reply.total : 0, message: reply.message, ...(reply.gpu !== undefined ? { gpu: reply.gpu } : {}) });
      else if (reply.type === 'error') finish(new Error(reply.message));
      else finish(undefined, { bytes: reply.bytes, files: reply.files });
    };
    worker.onerror = (event) => {
      event.preventDefault();
      finish(new Error('The video renderer stopped. Try a smaller size, or render it in the cloud.'));
    };
    worker.onmessageerror = () => finish(new Error('The video could not be read back. Please try again.'));
    worker.postMessage(snapshot);
  });
}

/** What the editor hands its export page (`ExportRoute`), and what the page needs to go to the cloud instead. */
export interface ExportJob {
  readonly snapshot: VideoSnapshot;
  readonly documentName: string;
  /** The file this makes: one video, or a zip of several. */
  readonly fileName: string;
  /** A small picture of the first artboard, for the page to show while it works. */
  readonly cover?: Uint8Array;
  /** The same export, asked of the render service. */
  readonly cloud: { readonly pageId: string; readonly targets: { pageId: string; artboardId: string }[]; readonly zipName: string; readonly scale: number };
}
